import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IProfileField, ProfileFieldType, updateProfileField } from '@/shared/api/profilebuilder'
import { Controller, FieldError, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import DotsSvg from '@/shared/assets/images/DotsSixVertical.svg'
import ReactSelect from 'react-select'
import ArrowDown from '@/shared/assets/images/ArrowDown.svg'
import ArrowUp from '@/shared/assets/images/ArrowUp.svg'
import CheckSvg from '@/shared/assets/images/Check.svg'
import DeleteSvg from '@/shared/assets/images/Trash.svg'
import notify from '@/shared/lib/notify'
import { DateForm, fieldTypeSelectOptions, FileForm, FormbuilderInput, formTypes, getValidationRules, NumberForm, StringForm, updateProfileFieldSchema } from '@/entities/formbuilder'

interface FormFieldUpdateFormValues {
    title: string
    required: boolean
    type: ProfileFieldType
    min_length?: number
    max_length?: number
    min_number?: number
    max_number?: number
    min_date?: Date
    max_date?: Date
    allowed_extensions?: string[]
}

const initialState: FormFieldUpdateFormValues = {
    title: '',
    required: false,
    type: 'string'
}

interface FormFieldProps {
    field: IProfileField
    onUpdate: () => void
    handleDelete: () => void
    index: number
}

export const FormField: React.FC<FormFieldProps> = ({ field, index, onUpdate, handleDelete }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const formRef = useRef<HTMLFormElement>(null)

    const onSubmit = async (data: typeof initialState) => {
        await updateProfileField(field.id, {
            type: data.type,
            title: data.title,
            editable: true,
            required: data.required,
            place_number: index,
            validation_rules: getValidationRules(data.type, data)
        })
        .then(() => {
            notify.success('Сохранено')
            onUpdate()
        })
        .catch(() => {
            notify.error('Не удалось сохранить изменения')
        })
    }

    const {
        register,
        handleSubmit,
        control,
        getValues,
        watch,
        formState: { errors },
        reset
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(updateProfileFieldSchema)
    })

    const form = useMemo(() => {
        const type = getValues('type')
        switch (type) {
            case "string":
            case "text":
                return <StringForm errors={errors} register={register} />
            case "natural_number":
            case "integer":
                return <NumberForm errors={errors} register={register} />
            case "date":
                return <DateForm errors={errors} control={control} />
            case "file":
                return <FileForm errors={errors} control={control} />
            default:
                return null
        }
    }, [watch, register, control, errors])

    useEffect(() => {
        reset({
            title: field.title,
            required: field.required,
            type: field.type,
            min_length: field.validation_rules.min_length,
            max_length: field.validation_rules.max_length,
            min_number: field.validation_rules.min_number,
            max_number: field.validation_rules.max_number,
            min_date: field.validation_rules.min_date ? new Date(field.validation_rules.min_date) : undefined,
            max_date: field.validation_rules.max_date ? new Date(field.validation_rules.max_date) : undefined,
            allowed_extensions: field.validation_rules.allowed_extensions
        })
    }, [field])

    useEffect(() => {
        reset((prev) => ({
            ...prev,
            min_length: field.validation_rules.min_length,
            max_length: field.validation_rules.max_length,
            min_number: field.validation_rules.min_number,
            max_number: field.validation_rules.max_number,
            min_date: field.validation_rules.min_date ? new Date(field.validation_rules.min_date) : undefined,
            max_date: field.validation_rules.max_date ? new Date(field.validation_rules.max_date) : undefined,
            allowed_extensions: field.validation_rules.allowed_extensions
        }))
    }, [watch('type')])

    return (
        <div className={`flex flex-col w-full bg-[#FAFAFA] rounded-lg`}>
            <div className={`flex justify-between px-2 py-[18px] ${isOpen ? 'border-b border-[#B9C9C6]' : ''}`}>
                <div className='flex items-center gap-[6px]'>
                    <DotsSvg height={24} width={24} />
                    <div className='text-[13px] font-semibold text-color-dark'>
                        №{index + 1} {field.title} <span className='text-[#828282]'>({formTypes[field.type as keyof typeof formTypes]})</span>
                    </div>
                </div>
                <div className='flex items-center gap-3'>
                    <button onClick={handleDelete}>
                        <DeleteSvg width={20} height={20} />
                    </button>
                    <button onClick={() => setIsOpen(prev => !prev)}>
                        <ArrowUp width={20} height={20} className={`transition  ${isOpen ? 'rotate-0' : 'rotate-180'}`} />
                    </button>
                </div>
            </div>
            <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className={`flex transition-all flex-col overflow-hidden ease-out ${isOpen ? 'max-h-[500px]' : 'max-h-[0px]'}`}>
                <div className='px-6 py-3'>
                    <Controller
                        control={control}
                        name={'type'}
                        render={({ field }) => (
                            <ReactSelect
                                className={`w-full rounded-lg text-color-dark flex items-center px-3 py-[10px] h-[60px] bg-white`}
                                options={fieldTypeSelectOptions}
                                classNames={{
                                    control: () => '!border-0 !p-0 !outline-0 !min-h-[auto] w-full !border-transparent !shadow-none',
                                    valueContainer: () => '!border-0 !outline-0 !p-0',
                                    menu: () => '!left-[0]',
                                    singleValue: () => 'absolute bottom-0 font-semibold',
                                    input: () => '!m-0 !p-0'
                                }}
                                noOptionsMessage={() => 'Нет опций'}
                                classNamePrefix="react_select_custom"
                                menuPosition='fixed'
                                menuPortalTarget={document.body}
                                components={{
                                    DropdownIndicator: () => <ArrowDown height={20} width={20} />,
                                    IndicatorSeparator: () => null,
                                    ValueContainer: (props) => (
                                        <div className='flex flex-col'>
                                            <div className='mb-2 text-[#828282] text-[12px]'>ТИП</div>
                                            {props.children}
                                        </div>
                                    )
                                }}
                                onChange={(e) => field.onChange(e.value)}
                                value={fieldTypeSelectOptions.find(option => option.value === field.value)}
                            />
                        )}
                    />
                </div>
                <div className='px-6 py-3'>
                    <FormbuilderInput
                        register={register}
                        label={"Название поля"}
                        id={"title"}
                        error={errors['title'] as FieldError}
                        type="text"
                        required={true}
                        placeholder="Введите название поля"
                    />
                    {form}
                    <div className='flex items-center py-[15px] gap-[10px]'>
                        <div className='relative flex h-full items-center'>
                            <input
                                {...register('required')}
                                type="checkbox"
                                className="w-6 h-6 cursor-pointer rounded-full border border-color-dark bg-white transition checked:bg-color-dark appearance-none"
                            />
                            <div className='absolute pointer-events-none text-white w-full h-full top-0 items-center justify-center flex'>
                                <CheckSvg width={16} height={16} />
                            </div>
                        </div>
                        <div className='font-semibold text-[13px] uppercase'>
                            Обязательное поле для заполнения
                        </div>
                    </div>
                    <button className="form_handle_button" type="submit">
                        Сохранить
                    </button>
                </div>
            </form>
        </div>
    )
}